import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { ref, uploadBytes, getBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from './firebase';
import { Project, ProjectFile, SharedProject } from '../types';

const PROJECTS_COLLECTION = 'projects';
const FILES_COLLECTION = 'files';

export type ProjectTemplate = 'basic' | 'open-problems' | 'open-problems-lean';

const MAX_BATCH_WRITES = 450;

/** Files larger than this threshold are stored in Cloud Storage instead of inline in Firestore. */
export const LARGE_FILE_THRESHOLD_BYTES = 500 * 1024;

export function isStorageBackedFile(file: ProjectFile): boolean {
  return !!file.storageContentPath;
}

function getFileStoragePath(projectId: string, fileId: string): string {
  return `projects/${projectId}/files/${fileId}/content.dat`;
}

export async function uploadFileContentToStorage(
  projectId: string,
  fileId: string,
  data: ArrayBuffer
): Promise<string> {
  const storagePath = getFileStoragePath(projectId, fileId);
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, new Uint8Array(data));
  return storagePath;
}

export async function downloadFileContentFromStorage(storagePath: string): Promise<string> {
  const storageRef = ref(storage, storagePath);
  const bytes = await getBytes(storageRef);
  return new TextDecoder().decode(bytes);
}

export async function deleteFileStorageContent(storagePath: string): Promise<void> {
  const storageRef = ref(storage, storagePath);
  await deleteObject(storageRef);
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface TemplateFile {
  name: string;
  content: string;
  type: 'tex' | 'bib' | 'other';
}

const BASIC_TEMPLATE: TemplateFile[] = [
  {
    name: 'main.tex',
    content: `\\documentclass{article}
\\usepackage{amsmath}
\\usepackage{graphicx}

\\begin{document}

\\title{My Document}
\\author{Your Name}
\\maketitle

\\section{Introduction}
Start writing here...

As demonstrated by Knuth \\cite{knuth1984}, literate programming is a powerful paradigm.

\\bibliographystyle{unsrt}
\\bibliography{refs}

\\end{document}`,
    type: 'tex',
  },
  {
    name: 'refs.bib',
    content: `@article{knuth1984,
  author  = {Donald E. Knuth},
  title   = {Literate Programming},
  journal = {The Computer Journal},
  volume  = {27},
  number  = {2},
  pages   = {97--111},
  year    = {1984},
}
`,
    type: 'bib',
  },
];

const OPEN_PROBLEMS_TEMPLATE: TemplateFile[] = [
  {
    name: 'main.tex',
    content: `\\documentclass[11pt]{article}
\\usepackage{amsmath}
\\usepackage{graphicx}
\\usepackage{verbatim}
\\usepackage{fvextra}
\\usepackage{geometry}
\\usepackage{enumitem}

\\geometry{margin=1in}

\\DefineVerbatimEnvironment{MyVerbatim}{Verbatim}{
    breaklines,
    breakanywhere,
    fontsize=\\small
}

\\usepackage{hyperref}
\\hypersetup{
    colorlinks=true,       
    linkcolor=red,          
    citecolor=magenta,       
    filecolor=red, 
    urlcolor=cyan
}

\\usepackage[T1]{fontenc}
\\usepackage[utf8]{inputenc}
\\usepackage{listings}
\\usepackage{xcolor}
\\usepackage{upquote}
\\definecolor{codegreen}{rgb}{0,0.6,0}
\\definecolor{codegray}{rgb}{0.5,0.5,0.5}
\\definecolor{codepurple}{rgb}{0.58,0,0.82}

% style for Python code
\\lstdefinestyle{pythonstyle}{
    language=Python,
    commentstyle=\\color{codegreen},
    keywordstyle=\\color{blue},
    numberstyle=\\tiny\\color{codegray},
    stringstyle=\\color{codepurple},
    basicstyle=\\ttfamily,     
    breaklines=true,                 
    numbers=left,                    
    showstringspaces=false         
}

\\begin{document}

\\title{My Document}
\\author{Your Name}
\\maketitle

\\section{Introduction}
Start writing here... As demonstrated by Knuth \\cite{knuth1984}, literate programming is a powerful paradigm.

\\section{Prompt}
\\input{prompt}

\\section{Verifier}
\\lstinputlisting[style=pythonstyle]{verifier.py}

\\section{Evaluation Questions}
\\input{evaluation_questions}

\\bibliographystyle{unsrt}
\\bibliography{refs}

\\end{document}`,
    type: 'tex',
  },
  {
    name: 'refs.bib',
    content: String.raw`@article{knuth1984,
  author  = {Donald E. Knuth},
  title   = {Literate Programming},
  journal = {The Computer Journal},
  volume  = {27},
  number  = {2},
  pages   = {97--111},
  year    = {1984},
}
`,
    type: 'bib',
  },
  {
    name: 'README.md',
    content: `## README

This project is set up with the Open Questions Template. 

Each contributed problem must meet the following criteria:

- **History:** The problem must have appeared in the literature before.
- **Difficulty:** At least two professional physicists must have tried to solve it.
- **Verifiability:** It must be possible to evaluate a proposed solution completely programmatically.
- **Notability:** A solution would be publishable in at least a standard specialty journal, or equivalent.
- **Solvability:** There must be good reason to believe the problem can be solved as stated (e.g., is not mathematically impossible or utterly computationally intractable).

To the complete the project you need to do the following:
- Write an introduction. Your introduction should discuss the criteria above and talk about what the problem is and why its important. (main.tex)
- Write the prompt an AI would use to attempt a solution (prompt.tex)
- Write code that can verify a solution (verifier.py)
- Answer the required evaluations questions (evaluation_questions.tex)

### Need help with LaTeX or AI assistance with the problem?
To edit files using AI-assistance
1. Load the file you want to edit by click on it in the files side bar.
2. Use the AI-assistance bar at the bottom of the editor ask it to add, remove or fix content
3. Accept the edits in the diff panel

You can also just chat with an AI model which will load your main.tex file in its context. To do so, click on the "Chats" header in the file sidebar and chat with the AI.

### Compilation
When you've completed each of the documents in the files panel on your left, compile the PDF by selecting main.tex and pressing the compile button at the top of the editor.

### Running the verifier
When you have completed the verifier, you can use the integrated verification environment to run your code. It will take the content of prompt.tex and send it to an AI model that we own (a Qwen 3 variant). You can use the output of the model by accessing stdin (\`raw = sys.stdin.read().strip()\`) for example. 

All problems will have to have working verifiers before the final submission.

### Submitting the problem
When you are happy with the problem, submit it for review using the "Submit for Review" button in the upper right.

### Time tracking
Keep an honest and faithful account of the time spend writing your problem using the timesheet functionality in the campaign in the Aligned platform. You will be required to update the timesheet by Sunday evening at 11:59PM PT.
`,
    type: 'other',
  },
  {
    name: 'prompt.tex',
    content: `% EXAMPLE PROMPT %
A projectile is launched from ground level ($y = 0$) on a flat plane with no air resistance. The motion is governed by standard kinematic equations under uniform gravitational acceleration $g = 9.80665\\;\\text{m/s}^2$.
 
The projectile's position at time $t$ is given by:
\\begin{align}
    x(t) &= v_0 \\cos(\\theta)\\cdot t \\\\[6pt]
    y(t) &= v_0 \\sin(\\theta)\\cdot t - \\frac{1}{2}\\,g\\,t^2
\\end{align}
where $v_0$ is the initial launch speed (in m/s) and $\theta$ is the launch angle measured from the horizontal (in radians).
 
You are given the following observed data from a single projectile launch:
 
\\begin{itemize}[itemsep=4pt]
    \\item \\textbf{Horizontal range} (total horizontal distance traveled before returning to $y=0$): $R = 79.479\\;\\text{m}$
    \\item \\textbf{Maximum height} (peak vertical displacement): $H = 34.415\\;\\text{m}$
    \\item \\textbf{Total time of flight} (time from launch to landing at $y=0$): $T = 5.299\\;\\text{s}$
\\end{itemize}
 
All three measurements are exact (no measurement noise).
 
\\medskip
\\noindent\\textbf{Your task:} Determine the initial launch speed $v_0$ and launch angle $\\theta$ that produce exactly these observed values.
 
\\subsection{Constraints}
 
\\begin{itemize}[itemsep=4pt]
    \\item $v_0 > 0$
    \\item $0 < \\theta < \\dfrac{\\pi}{2}$ \\;(the projectile is launched above horizontal)
    \\item Use $g = 9.80665\\;\\text{m/s}^2$ exactly
    \\item Your solution must reproduce all three observed values ($R$, $H$, $T$) to within an absolute tolerance of $10^{-3}$
\\end{itemize}
 
\\subsection{Reference Formulas}
 
For convenience, the closed-form expressions for the observables in terms of $v_0$ and $\theta$ are:
 
\\begin{align}
    R &= \\frac{v_0^2 \\sin(2\\theta)}{g} \\\\[8pt]
    H &= \\frac{v_0^2 \\sin^2(\\theta)}{2g} \\\\[8pt]
    T &= \\frac{2\\,v_0 \\sin(\\theta)}{g}
\\end{align}
 
\\newpage
\\section{Output Format}
 
Return your answer as a JSON object with the following structure:
 
\\begin{lstlisting}
{
  "v0": 28.50,
  "theta": 1.012,
  "units": {
    "v0": "m/s",
    "theta": "radians"
  }
}
\\end{lstlisting}
 
\\subsection{Field Definitions}
 
\\begin{itemize}[itemsep=4pt]
    \\item \\texttt{v0} (float): The initial launch speed in meters per second. Must be positive.
    \\item \\texttt{theta} (float): The launch angle in radians. Must be strictly between $0$ and $\\pi/2$ (exclusive).
    \\item \\texttt{units} (object): A confirmation of the units used. Must be exactly \\texttt{\\{"v0": "m/s", "theta": "radians"\\}}.
\\end{itemize}
 
\\subsection{Notes}
 
\\begin{itemize}[itemsep=4pt]
    \\item The example output above is \\textbf{illustrative only} and is \\textbf{NOT} the correct solution---do not copy it.
    \\item Report \\texttt{v0} and \\texttt{theta} to at least 4 decimal places.
    \\item The verifier will plug your values into the three reference formulas above and check that the resulting $R$, $H$, and $T$ each match the observed values to within $\\pm\\,0.001$.
\\end{itemize}`,
    type: 'tex',
  },
  {
    name: 'verifier.py',
    content: String.raw`#!/usr/bin/env python3
"""
Example working Verifier for the Projectile Motion Inverse Problem. (Replace this with your code).

Reads a candidate solution from stdin as a JSON object with fields:
  - v0 (float): initial launch speed in m/s
  - theta (float): launch angle in radians
  - units (object): must be {"v0": "m/s", "theta": "radians"}

Checks that the proposed (v0, theta) reproduce the observed data:
  - R = 79.479 m  (horizontal range)
  - H = 34.415 m  (maximum height)
  - T = 5.299 s   (total time of flight)

All three must match to within an absolute tolerance of 1e-3.

Usage:
  echo '{"v0": 24.467, "theta": 1.047, "units": {"v0": "m/s", "theta": "radians"}}' | python verifier.py

Exit codes:
  0 = PASS (solution is correct)
  1 = FAIL (solution is incorrect or invalid)
"""

import sys
import json
import math

# =============================================================================
# Ground-truth observed data
# =============================================================================
G = 9.80665       # gravitational acceleration (m/s^2)
R_OBS = 79.479    # horizontal range (m)
H_OBS = 34.415    # maximum height (m)
T_OBS = 5.299     # total time of flight (s)
TOL = 1e-3        # absolute tolerance


def forward_model(v0: float, theta: float) -> dict:
    """
    Compute the three observables from the launch parameters.

    Parameters
    ----------
    v0 : float
        Initial launch speed (m/s).
    theta : float
        Launch angle (radians).

    Returns
    -------
    dict with keys 'R', 'H', 'T'.
    """
    R = (v0 ** 2) * math.sin(2.0 * theta) / G
    H = (v0 ** 2) * (math.sin(theta) ** 2) / (2.0 * G)
    T = 2.0 * v0 * math.sin(theta) / G
    return {"R": R, "H": H, "T": T}


def validate_input(data: dict) -> list:
    """
    Validate the structure and types of the input JSON.

    Returns a list of error strings. Empty list means valid.
    """
    errors = []

    # --- Check required fields exist ---
    for field in ("v0", "theta", "units"):
        if field not in data:
            errors.append(f"Missing required field: '{field}'")

    if errors:
        return errors  # Can't proceed without required fields

    # --- Check types ---
    if not isinstance(data["v0"], (int, float)):
        errors.append(f"'v0' must be a number, got {type(data['v0']).__name__}")
    if not isinstance(data["theta"], (int, float)):
        errors.append(f"'theta' must be a number, got {type(data['theta']).__name__}")
    if not isinstance(data["units"], dict):
        errors.append(f"'units' must be an object, got {type(data['units']).__name__}")

    if errors:
        return errors  # Can't proceed with wrong types

    # --- Check numeric values are finite ---
    if not math.isfinite(data["v0"]):
        errors.append(f"'v0' must be finite, got {data['v0']}")
    if not math.isfinite(data["theta"]):
        errors.append(f"'theta' must be finite, got {data['theta']}")

    # --- Check physical constraints ---
    if isinstance(data["v0"], (int, float)) and math.isfinite(data["v0"]):
        if data["v0"] <= 0:
            errors.append(f"'v0' must be positive, got {data['v0']}")

    if isinstance(data["theta"], (int, float)) and math.isfinite(data["theta"]):
        if data["theta"] <= 0 or data["theta"] >= math.pi / 2:
            errors.append(
                f"'theta' must be in (0, pi/2), got {data['theta']}"
            )

    # --- Check units ---
    expected_units = {"v0": "m/s", "theta": "radians"}
    if data["units"] != expected_units:
        errors.append(
            f"'units' must be {expected_units}, got {data['units']}"
        )

    return errors


def verify(data: dict) -> tuple:
    """
    Run the full verification.

    Returns
    -------
    (passed: bool, details: dict)
    """
    # Validate input structure
    input_errors = validate_input(data)
    if input_errors:
        return False, {"status": "INVALID_INPUT", "errors": input_errors}

    v0 = float(data["v0"])
    theta = float(data["theta"])

    # Compute observables from proposed parameters
    computed = forward_model(v0, theta)

    # Compare against observed data
    checks = {
        "R": {"computed": computed["R"], "expected": R_OBS, "tol": TOL},
        "H": {"computed": computed["H"], "expected": H_OBS, "tol": TOL},
        "T": {"computed": computed["T"], "expected": T_OBS, "tol": TOL},
    }

    all_pass = True
    for key, check in checks.items():
        diff = abs(check["computed"] - check["expected"])
        check["abs_error"] = diff
        check["pass"] = diff <= check["tol"]
        if not check["pass"]:
            all_pass = False

    return all_pass, {
        "status": "PASS" if all_pass else "FAIL",
        "v0": v0,
        "theta": theta,
        "checks": checks,
    }


def main():
    # --- Read input from stdin ---
    try:
        raw = sys.stdin.read().strip()
    except Exception as e:
        print(json.dumps({"status": "ERROR", "message": f"Failed to read stdin: {e}"}))
        sys.exit(1)

    if not raw:
        print(json.dumps({"status": "ERROR", "message": "No input received on stdin."}))
        sys.exit(1)

    # --- Parse JSON ---
    try:
        data = json.loads(raw)
    except json.JSONDecodeError as e:
        print(json.dumps({"status": "ERROR", "message": f"Invalid JSON: {e}"}))
        sys.exit(1)

    if not isinstance(data, dict):
        print(json.dumps({"status": "ERROR", "message": f"Expected a JSON object, got {type(data).__name__}"}))
        sys.exit(1)

    # --- Run verification ---
    passed, details = verify(data)

    # --- Output results ---
    print(json.dumps(details, indent=2))
    sys.exit(0 if passed else 1)


if __name__ == "__main__":
    main()
`,
    type: 'other',
  },
  {
    name: 'evaluation_questions.tex',
    content: `\\begin{enumerate}
\\setcounter{enumi}{1}
\\item If a human were to solve this problem, the solution would be considered:
\\begin{itemize}
\\item More trivia than a real result
\\item Moderately interesting to a subfield
\\item A solid result in a subfield
\\item A major advance in a subfield
\\item A major advance in a broader field; one of the best results of the year
\\item A major result in mathematics, of broad interest to multiple fields
\\end{itemize}
\\textit{Your Selection:} Your selection here

\\textit{Explanation:} Add your explanation here.
\\end{enumerate}

\\begin{enumerate}
\\setcounter{enumi}{2}
\\item If a human were to solve this problem, the solution would be published...
\\begin{itemize}
\\item Not at all / only on personal blogs, social media, etc.
\\item In a minor specialty journal
\\item In a standard specialty journal
\\item In a strong specialty journal
\\item In a top journal for the field
\\item In a leading general journal
\\end{itemize}
\\textit{Your Selection:} Your selection here

\\textit{Explanation:} Add your explanation here.
\\end{enumerate}

\\begin{enumerate}
\\setcounter{enumi}{3}
\\item All things considered, including the verification-amenable formulation, how likely do you think the problem is to be solvable as stated?
\\begin{itemize}
\\item 40\\%
\\item 40--60\\%
\\item 60--80\\%
\\item 80--95\\%
\\item 95--99\\%
\\item $>$99\\%\\%
\\end{itemize}
\\textit{Your Selection:} Your selection here

\\textit{Explanation:} Add your explanation here.
\\end{enumerate}`,
    type: 'other',
  },
];

const OPEN_PROBLEMS_LEAN_TEMPLATE: TemplateFile[] = [
  {
    name: 'main.tex',
    content: `\\documentclass[11pt]{article}
\\usepackage{amsmath}
\\usepackage{graphicx}
\\usepackage{verbatim}
\\usepackage{fvextra}
\\usepackage{geometry}
\\usepackage{enumitem}

\\geometry{margin=1in}

\\DefineVerbatimEnvironment{MyVerbatim}{Verbatim}{
    breaklines,
    breakanywhere,
    fontsize=\\small
}

\\usepackage{hyperref}
\\hypersetup{
    colorlinks=true,
    linkcolor=red,
    citecolor=magenta,
    filecolor=red,
    urlcolor=cyan
}

\\usepackage[T1]{fontenc}
\\usepackage[utf8]{inputenc}
\\usepackage{listings}
\\usepackage{xcolor}
\\definecolor{codegreen}{rgb}{0,0.6,0}
\\definecolor{codegray}{rgb}{0.5,0.5,0.5}
\\definecolor{codepurple}{rgb}{0.58,0,0.82}

% style for Lean code
\\lstdefinestyle{leanstyle}{
    commentstyle=\\color{codegreen},
    keywordstyle=\\color{blue},
    numberstyle=\\tiny\\color{codegray},
    stringstyle=\\color{codepurple},
    basicstyle=\\ttfamily,
    breaklines=true,
    numbers=left,
    showstringspaces=false,
    morekeywords={theorem,lemma,def,by,import,open,namespace,end,where,have,let,show,calc,simp,omega,ring,linarith,norm_num,sorry,example,section}
}

\\begin{document}

\\title{My Document}
\\author{Your Name}
\\maketitle

\\section{Introduction}
Start writing here... As demonstrated by Knuth \\cite{knuth1984}, literate programming is a powerful paradigm.

\\section{Prompt}
\\input{prompt}

\\section{Verifier}
\\lstinputlisting[style=leanstyle]{verifier.lean}

\\section{Evaluation Questions}
\\input{evaluation_questions}

\\bibliographystyle{unsrt}
\\bibliography{refs}

\\end{document}`,
    type: 'tex',
  },
  {
    name: 'refs.bib',
    content: String.raw`@article{knuth1984,
  author  = {Donald E. Knuth},
  title   = {Literate Programming},
  journal = {The Computer Journal},
  volume  = {27},
  number  = {2},
  pages   = {97--111},
  year    = {1984},
}
`,
    type: 'bib',
  },
  {
    name: 'prompt.tex',
    content: `% EXAMPLE PROMPT — replace with your theorem statement %
Prove the following theorem in Lean 4 using Mathlib.

Theorem: For all natural numbers $n$, $0 + n = n$.

Return only valid Lean 4 code. The code must compile without errors or sorries.
Do not include any import statements --- they are already provided in the verifier file.
Your proof will be appended to the imports in \\texttt{verifier.lean}.

Start your response with the \\texttt{theorem} keyword. Use a unique theorem name
that is unlikely to clash with existing Mathlib declarations.`,
    type: 'tex',
  },
  {
    name: 'verifier.lean',
    content: `import Mathlib

open Classical

-- The Lean verifier entrypoint.
-- The AI model's proof output is appended after these imports
-- and checked by the Lean 4 server with full Mathlib available.
-- Add any additional imports or setup your theorem requires here.
`,
    type: 'other',
  },
  {
    name: 'evaluation_questions.tex',
    content: `\\begin{enumerate}
\\setcounter{enumi}{1}
\\item If a human were to solve this problem, the solution would be considered:
\\begin{itemize}
\\item More trivia than a real result
\\item Moderately interesting to a subfield
\\item A solid result in a subfield
\\item A major advance in a subfield
\\item A major advance in a broader field; one of the best results of the year
\\item A major result in mathematics, of broad interest to multiple fields
\\end{itemize}
\\textit{Your Selection:} Your selection here

\\textit{Explanation:} Add your explanation here.
\\end{enumerate}

\\begin{enumerate}
\\setcounter{enumi}{2}
\\item If a human were to solve this problem, the solution would be published...
\\begin{itemize}
\\item Not at all / only on personal blogs, social media, etc.
\\item In a minor specialty journal
\\item In a standard specialty journal
\\item In a strong specialty journal
\\item In a top journal for the field
\\item In a leading general journal
\\end{itemize}
\\textit{Your Selection:} Your selection here

\\textit{Explanation:} Add your explanation here.
\\end{enumerate}

\\begin{enumerate}
\\setcounter{enumi}{3}
\\item All things considered, including the verification-amenable formulation, how likely do you think the problem is to be solvable as stated?
\\begin{itemize}
\\item 40\\%
\\item 40--60\\%
\\item 60--80\\%
\\item 80--95\\%
\\item 95--99\\%
\\item $>$99\\%\\%
\\end{itemize}
\\textit{Your Selection:} Your selection here

\\textit{Explanation:} Add your explanation here.
\\end{enumerate}`,
    type: 'other',
  },
];

const TEMPLATES: Record<ProjectTemplate, TemplateFile[]> = {
  'basic': BASIC_TEMPLATE,
  'open-problems': OPEN_PROBLEMS_TEMPLATE,
  'open-problems-lean': OPEN_PROBLEMS_LEAN_TEMPLATE,
};

export async function createProject(
  ownerId: string,
  title: string,
  template: ProjectTemplate = 'basic',
  tags: string[] = []
): Promise<string> {
  const now = Timestamp.now();
  const projectRef = await addDoc(collection(db, PROJECTS_COLLECTION), {
    ownerId,
    title,
    folder: null,
    reviewEnabled: template === 'open-problems' || template === 'open-problems-lean',
    tags,
    createdAt: now,
    updatedAt: now,
  });

  const filesCollection = collection(db, PROJECTS_COLLECTION, projectRef.id, FILES_COLLECTION);
  const templateFiles = TEMPLATES[template];

  await Promise.all(
    templateFiles.map((file) =>
      addDoc(filesCollection, {
        name: file.name,
        content: file.content,
        type: file.type,
        parentId: null,
        version: 0,
        otEnabled: true,
      })
    )
  );

  return projectRef.id;
}

export async function importProject(
  ownerId: string,
  title: string,
  fileName: string,
  content: string
): Promise<string> {
  const now = Timestamp.now();
  const projectRef = await addDoc(collection(db, PROJECTS_COLLECTION), {
    ownerId,
    title,
    folder: null,
    reviewEnabled: false,
    createdAt: now,
    updatedAt: now,
  });

  // Create file with imported content (OT-enabled)
  await addDoc(collection(db, PROJECTS_COLLECTION, projectRef.id, FILES_COLLECTION), {
    name: fileName,
    content,
    type: 'tex',
    parentId: null,
    version: 0,
    otEnabled: true,
  });

  return projectRef.id;
}

export async function getProjects(ownerId: string): Promise<Project[]> {
  const q = query(
    collection(db, PROJECTS_COLLECTION),
    where('ownerId', '==', ownerId),
    orderBy('updatedAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
    folder: doc.data().folder ?? null,
    createdAt: doc.data().createdAt?.toDate(),
    updatedAt: doc.data().updatedAt?.toDate(),
  })) as Project[];
}

export async function getSharedProjects(userId: string): Promise<SharedProject[]> {
  const q = query(
    collection(db, 'users', userId, 'sharedProjects'),
    orderBy('addedAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    projectId: doc.data().projectId || doc.id,
    ownerId: doc.data().ownerId,
    title: doc.data().title || 'Untitled Project',
    role: doc.data().role,
    addedAt: doc.data().addedAt?.toDate?.() ?? new Date(),
    updatedAt: doc.data().updatedAt?.toDate?.() ?? new Date(),
  })) as SharedProject[];
}

export async function getProject(projectId: string): Promise<Project | null> {
  const docRef = doc(db, PROJECTS_COLLECTION, projectId);
  const snapshot = await getDoc(docRef);
  if (!snapshot.exists()) return null;
  return {
    id: snapshot.id,
    ...snapshot.data(),
    folder: snapshot.data().folder ?? null,
    createdAt: snapshot.data().createdAt?.toDate(),
    updatedAt: snapshot.data().updatedAt?.toDate(),
  } as Project;
}

export async function updateProject(
  projectId: string,
  updates: Partial<Pick<Project, 'title' | 'folder' | 'reviewEnabled'>>
): Promise<void> {
  const docRef = doc(db, PROJECTS_COLLECTION, projectId);
  await updateDoc(docRef, {
    ...updates,
    updatedAt: Timestamp.now(),
  });
}

export async function deleteProject(projectId: string): Promise<void> {
  // Delete all files in the project first
  const filesSnapshot = await getDocs(
    collection(db, PROJECTS_COLLECTION, projectId, FILES_COLLECTION)
  );
  const deletePromises = filesSnapshot.docs.map((doc) => deleteDoc(doc.ref));
  await Promise.all(deletePromises);

  // Delete the project
  await deleteDoc(doc(db, PROJECTS_COLLECTION, projectId));
}

export async function getProjectFiles(projectId: string): Promise<ProjectFile[]> {
  const snapshot = await getDocs(
    collection(db, PROJECTS_COLLECTION, projectId, FILES_COLLECTION)
  );
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as ProjectFile[];
}

export async function copyProjectToOwner(
  sourceProjectId: string,
  ownerId: string,
  title: string
): Promise<string> {
  const sourceProject = await getProject(sourceProjectId);
  if (!sourceProject) {
    throw new Error('Source project not found');
  }

  const sourceFiles = await getProjectFiles(sourceProjectId);
  const now = Timestamp.now();
  const projectRef = await addDoc(collection(db, PROJECTS_COLLECTION), {
    ownerId,
    title,
    folder: null,
    reviewEnabled: sourceProject.reviewEnabled ?? false,
    createdAt: now,
    updatedAt: now,
  });

  const storageCopyQueue: Array<{ sourcePath: string; destFileId: string }> = [];

  for (let index = 0; index < sourceFiles.length; index += MAX_BATCH_WRITES) {
    const batch = writeBatch(db);
    const filesChunk = sourceFiles.slice(index, index + MAX_BATCH_WRITES);

    filesChunk.forEach((file) => {
      const fileRef = doc(collection(db, PROJECTS_COLLECTION, projectRef.id, FILES_COLLECTION));

      if (isStorageBackedFile(file) && file.storageContentPath) {
        batch.set(fileRef, {
          name: file.name,
          content: '',
          type: file.type,
          parentId: file.parentId ?? null,
          version: 0,
          otEnabled: false,
          storageContentPath: getFileStoragePath(projectRef.id, fileRef.id),
          storageSizeBytes: file.storageSizeBytes ?? 0,
        });
        storageCopyQueue.push({
          sourcePath: file.storageContentPath,
          destFileId: fileRef.id,
        });
      } else {
        batch.set(fileRef, {
          name: file.name,
          content: file.content,
          type: file.type,
          parentId: file.parentId ?? null,
          version: file.version ?? 0,
          otEnabled: file.otEnabled ?? true,
          ...(file.lastModifiedBy ? { lastModifiedBy: file.lastModifiedBy } : {}),
        });
      }
    });

    await batch.commit();
  }

  // Copy storage-backed file content to the new project
  for (const { sourcePath, destFileId } of storageCopyQueue) {
    const sourceRef = ref(storage, sourcePath);
    const sourceBytes = await getBytes(sourceRef);
    const destPath = getFileStoragePath(projectRef.id, destFileId);
    const destRef = ref(storage, destPath);
    await uploadBytes(destRef, new Uint8Array(sourceBytes));
  }

  return projectRef.id;
}

export async function createFile(
  projectId: string,
  file: Omit<ProjectFile, 'id'>
): Promise<string> {
  const docRef = await addDoc(
    collection(db, PROJECTS_COLLECTION, projectId, FILES_COLLECTION),
    {
      ...file,
      version: file.version ?? 0,
      otEnabled: file.otEnabled ?? true,
    }
  );

  // Update project timestamp
  await updateDoc(doc(db, PROJECTS_COLLECTION, projectId), {
    updatedAt: Timestamp.now(),
  });

  return docRef.id;
}

export async function updateFile(
  projectId: string,
  fileId: string,
  updates: Partial<Omit<ProjectFile, 'id'>>
): Promise<void> {
  const fileRef = doc(db, PROJECTS_COLLECTION, projectId, FILES_COLLECTION, fileId);
  await updateDoc(fileRef, updates);

  // Update project timestamp
  await updateDoc(doc(db, PROJECTS_COLLECTION, projectId), {
    updatedAt: Timestamp.now(),
  });
}

export async function deleteFile(projectId: string, fileId: string): Promise<void> {
  await deleteDoc(doc(db, PROJECTS_COLLECTION, projectId, FILES_COLLECTION, fileId));

  // Update project timestamp
  await updateDoc(doc(db, PROJECTS_COLLECTION, projectId), {
    updatedAt: Timestamp.now(),
  });
}

export async function uploadProjectImage(
  projectId: string,
  fileName: string,
  file: File
): Promise<string> {
  const storageRef = ref(storage, `projects/${projectId}/images/${fileName}`);
  await uploadBytes(storageRef, file);
  return getDownloadURL(storageRef);
}

export async function restoreFiles(
  projectId: string,
  filesToRestore: Array<{
    name: string;
    content: string;
    type: 'tex' | 'bib' | 'folder' | 'image' | 'other';
    parentId: string | null;
  }>
): Promise<void> {
  const filesCollectionRef = collection(db, PROJECTS_COLLECTION, projectId, FILES_COLLECTION);
  const existingFiles = await getDocs(filesCollectionRef);
  const now = Timestamp.now();

  // Clean up Storage content for any existing storage-backed files
  const storageCleanups: Promise<void>[] = [];
  for (const snapshot of existingFiles.docs) {
    const data = snapshot.data();
    if (data.storageContentPath) {
      storageCleanups.push(
        deleteFileStorageContent(data.storageContentPath).catch(() => {})
      );
    }
  }
  await Promise.all(storageCleanups);

  const deleteOps = existingFiles.docs.map((snapshot) => ({
    type: 'delete' as const,
    ref: snapshot.ref,
  }));

  // Separate files into inline vs storage-backed based on content size
  const createOps: Array<{
    type: 'set';
    ref: ReturnType<typeof doc>;
    data: Record<string, unknown>;
  }> = [];

  const storageUploads: Array<{
    fileRef: ReturnType<typeof doc>;
    content: string;
  }> = [];

  for (const file of filesToRestore) {
    const fileRef = doc(filesCollectionRef);
    const contentBytes = new TextEncoder().encode(file.content).byteLength;
    const isLarge = file.type !== 'folder' && file.type !== 'image'
      && contentBytes > LARGE_FILE_THRESHOLD_BYTES;

    if (isLarge) {
      createOps.push({
        type: 'set',
        ref: fileRef,
        data: {
          name: file.name,
          content: '',
          type: file.type,
          parentId: file.parentId ?? null,
          version: 0,
          otEnabled: false,
          storageContentPath: getFileStoragePath(projectId, fileRef.id),
          storageSizeBytes: contentBytes,
        },
      });
      storageUploads.push({ fileRef, content: file.content });
    } else {
      createOps.push({
        type: 'set',
        ref: fileRef,
        data: {
          name: file.name,
          content: file.content,
          type: file.type,
          parentId: file.parentId ?? null,
          version: 0,
          otEnabled: true,
        },
      });
    }
  }

  const operations = [...deleteOps, ...createOps];

  if (operations.length === 0) {
    await updateDoc(doc(db, PROJECTS_COLLECTION, projectId), {
      updatedAt: now,
    });
    return;
  }

  for (let index = 0; index < operations.length; index += MAX_BATCH_WRITES) {
    const batch = writeBatch(db);
    const chunk = operations.slice(index, index + MAX_BATCH_WRITES);

    chunk.forEach((operation) => {
      if (operation.type === 'delete') {
        batch.delete(operation.ref);
        return;
      }

      batch.set(operation.ref, operation.data);
    });

    await batch.commit();
  }

  // Upload large file content to Storage
  for (const upload of storageUploads) {
    const data = new TextEncoder().encode(upload.content);
    await uploadFileContentToStorage(projectId, upload.fileRef.id, data.buffer as ArrayBuffer);
  }

  await updateDoc(doc(db, PROJECTS_COLLECTION, projectId), {
    updatedAt: now,
  });
}
